import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import pencilTokens from "@amzn/meridian-tokens/base/icon/pencil";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import { addedRowHighlightClass } from "../../../styles/motion";
import { gridStackClass } from "../insights.styles";
import DeleteConfirmButtons from "../DeleteConfirmButtons";
import HeightReserver from "../HeightReserver";
import { rowTitleClass, strikethroughClass } from "./agenda.styles";
import IconButton from "../IconButton";
import { useHoverReveal } from "../insights.hooks";
import { AGENDA_STATE, IN_PROGRESS_LABEL, IN_PROGRESS_TITLE_TOKENS, LABEL_ROW_WIDTHS, ROW_LINE_HEIGHT } from "./agenda.constants";
import { DELETE_CONFIRM_LABEL, deleteButtonTheme } from "../insights.constants";

// Title text — shared between DisplayRow and ConfirmDeleteRow so height parity is guaranteed.
const TitleContent = ({ item }) => {
  const isCompleted = item.state === AGENDA_STATE.COMPLETED;
  const isInProgress = item.state === AGENDA_STATE.IN_PROGRESS;

  if (isInProgress) {
    return (
      <div className={rowTitleClass}>
        <Theme tokens={IN_PROGRESS_TITLE_TOKENS}>
          <Text type="b200">{item.title}</Text>
        </Theme>
      </div>
    );
  }

  return (
    <div className={rowTitleClass}>
      <Text
        type="b200"
        className={isCompleted ? strikethroughClass : undefined}
        color={isCompleted ? "secondary" : "primary"}
      >
        {item.title}
      </Text>
    </div>
  );
};

// Display state: title + edit/delete controls (hover-revealed) + optional "In progress" label.
// The controls and "In progress" label crossfade in the same space via gridStackClass.
const DisplayRow = ({ item, onEdit, onDelete, controlsStyle, restingStyle, spacingInset }) => {
  const isInProgress = item.state === AGENDA_STATE.IN_PROGRESS;

  return (
    <Row width="100%" widths={LABEL_ROW_WIDTHS} spacing="200" alignmentVertical="top" spacingInset={spacingInset}>
      <TitleContent item={item} />
      <div className={gridStackClass}>
        <Row height={ROW_LINE_HEIGHT} spacing="200" alignmentVertical="center" alignmentHorizontal="end">
          <IconButton icon={pencilTokens} onClick={onEdit} ariaLabel="Edit" style={controlsStyle} />
          <IconButton icon={trashTokens} onClick={onDelete} ariaLabel="Delete" tokens={deleteButtonTheme} style={controlsStyle} />
        </Row>
        {isInProgress && (
          <div style={restingStyle}>
            <Row height={ROW_LINE_HEIGHT} alignmentVertical="center" alignmentHorizontal="end">
              <Text type="b100" color="secondary">{IN_PROGRESS_LABEL}</Text>
            </Row>
          </div>
        )}
      </div>
    </Row>
  );
};

// Delete-confirm state: hidden title reserves height, prompt overlays it. Buttons are direct
// Row children — no overlay needed (matches ActionItemCard's ConfirmDeleteRow).
const ConfirmDeleteRow = ({ item, onConfirmDelete, onCancelDelete, spacingInset }) => (
  <Row width="100%" widths={LABEL_ROW_WIDTHS} spacing="200" alignmentVertical="top" spacingInset={spacingInset}>
    <div className={gridStackClass}>
      <HeightReserver>
        <TitleContent item={item} />
      </HeightReserver>
      <Row alignmentVertical="top" spacing="none">
        <Text type="b200" color="secondary">{DELETE_CONFIRM_LABEL}</Text>
      </Row>
    </div>
    <Row height={ROW_LINE_HEIGHT} spacing="200" alignmentVertical="center" alignmentHorizontal="end">
      <DeleteConfirmButtons onConfirm={onConfirmDelete} onCancel={onCancelDelete} />
    </Row>
  </Row>
);

/**
 * AgendaRowLabel - Non-editing label for a ProgressTracker step.
 * A `confirming` fork picks the whole row: ConfirmDeleteRow vs DisplayRow.
 * Both share TitleContent for height parity (matches ActionItemCard's pattern).
 */
const AgendaRowLabel = ({ item, onEdit, onDelete, onConfirmDelete, onCancelDelete, confirming, highlighted, spacingInset }) => {
  const { bind, controlsStyle, restingStyle } = useHoverReveal();

  return (
    <div style={{ width: "100%" }} {...bind}>
      <div className={highlighted ? addedRowHighlightClass : undefined}>
        {confirming ? (
          <ConfirmDeleteRow item={item} onConfirmDelete={onConfirmDelete} onCancelDelete={onCancelDelete} spacingInset={spacingInset} />
        ) : (
          <DisplayRow item={item} onEdit={onEdit} onDelete={onDelete} controlsStyle={controlsStyle} restingStyle={restingStyle} spacingInset={spacingInset} />
        )}
      </div>
    </div>
  );
};

export default AgendaRowLabel;
